import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { DashboardCardCP } from 'app/screens/screen-home/components/DashboardCardCP'
import { DashboardLinksUtils } from 'app/screens/screen-home/inner/DashboardLinksUtils'
import { CollectionStatusUtils } from 'modules/collection-performance/utils/CollectionStatusUtils'
import { MonthRangeTP, MonthRangeUtils } from 'modules/collection-performance/utils/MonthRangeUtils'
import { CollectionStatusEnum } from 'submodules/nerit-aquatec-sdk/common/enums/CollectionStatusEnum'
import { CollectionControlRequests } from 'submodules/nerit-aquatec-sdk/services/collection-control/CollectionControlRequests'
import { CollectionGridResponseDTO } from 'submodules/nerit-aquatec-sdk/services/collection-control/dtos/responses/CollectionGridResponseDTO'
import { MatrixRequests } from 'submodules/nerit-aquatec-sdk/services/matrix/MatrixRequests'
import { MatrixResponseDTO } from 'submodules/nerit-aquatec-sdk/services/matrix/dtos/responses/MatrixResponseDTO'
import { EmptyCP } from 'submodules/nerit-framework-ui/common/components/empty/EmptyCP'
import { SelectCP } from 'submodules/nerit-framework-ui/common/components/form-fields/select/SelectCP'
import { IDateRangeFilter } from 'submodules/nerit-framework-ui/common/components/form-fields/date-range-picker/inner/IDateRangeFilter'
import { ProgressCP } from 'submodules/nerit-framework-ui/common/components/progress/ProgressCP'
import { TagCP } from 'submodules/nerit-framework-ui/common/components/tag/TagCP'
import { TextCP } from 'submodules/nerit-framework-ui/common/components/text/TextCP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { ISearchResult } from 'submodules/nerit-framework-utils/sdk-utils/dtos/interfaces/ISearchResult'
import { ListResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/ListResponseDTO'
import { NameAndCodeResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/NameAndCodeResponseDTO'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'

interface ICPProps {
    dateRange: IDateRangeFilter
}

type CollectionSummaryTP = {
    locationCodes: number[],
    totalExpected: number,
    totalImported: number,
    totalsByStatus: Map<CollectionStatusEnum, number>,
}

/**
 * Desempenho de coleta: pontos previstos x importados da Matriz no período do Dashboard.
 * Previsto = cada ponto da Matriz em cada mês tocado pelo período (mesma regra da grade do Controle de Pontos).
 */
export function DashboardCollectionPerformanceCardCP(props: ICPProps): JSX.Element {

    const [matrices, setMatrices] = useState<MatrixResponseDTO[]>()
    const [matrixCode, setMatrixCode] = useState<number>()
    const [summary, setSummary] = useState<CollectionSummaryTP>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const monthRange = MonthRangeUtils.fromDateRange(props.dateRange)

    useEffect(() => { void loadMatrices() }, [])
    useEffect(() => { void loadSummary() }, [matrixCode, props.dateRange])

    /**
     * A Matriz padrão é a primeira registrada.
     */
    async function loadMatrices(): Promise<void> {

        try {
            const response = await RequestHelper.runRequest<IApiReturn<ISearchResult<MatrixResponseDTO>>>(
                MatrixRequests.search({ page: 1, itemsPerPage: 999 }),
            )
            const list = [...(response.data.data?.list ?? [])].sort((a, b) => a.code - b.code)
            setMatrices(list)
            setMatrixCode(list[0]?.code)
        } catch {
            setMatrices([])
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        }
    }

    /**
     */
    async function loadSummary(): Promise<void> {

        if (!matrixCode || !monthRange) {
            setSummary(undefined)
            return
        }

        setIsLoading(true)
        try {
            const locationsResponse = await RequestHelper.runRequest<IApiReturn<ListResponseDTO<NameAndCodeResponseDTO>>>(
                MatrixRequests.getLocations(matrixCode),
            )
            const locationCodes = (locationsResponse.data.data?.list ?? []).map((location) => location.code)
            if (locationCodes.length === 0) {
                setSummary({ locationCodes, totalExpected: 0, totalImported: 0, totalsByStatus: new Map() })
                return
            }

            const gridResponse = await RequestHelper.runRequest<IApiReturn<CollectionGridResponseDTO>>(
                CollectionControlRequests.grid({ locationCodes, ...monthRange }),
            )
            setSummary(summarize(locationCodes, gridResponse.data.data))
        } catch {
            setSummary(undefined)
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        } finally {
            setIsLoading(false)
        }
    }

    const importedPercent = !!summary && summary.totalExpected > 0
        ? Math.round((summary.totalImported / summary.totalExpected) * 100)
        : 0

    return (
        <DashboardCardCP
            title={'Previsto x Importado'}
            subtitle={'Cobertura por Matriz'}
            isLoading={isLoading}
            extra={
                !!matrices && matrices.length > 0
                    ? (
                        <SelectCP
                            label={'Matriz'}
                            width={220}
                            value={matrixCode}
                            options={matrices.map((matrix) => ({ value: matrix.code, label: matrix.name }))}
                            onChange={(code: number) => setMatrixCode(code)}
                        />
                    )
                    : undefined
            }
            link={{
                label: 'Controle de Pontos',
                onClick: () => DashboardLinksUtils.goToCollectionControl(matrixCode, summary?.locationCodes ?? [], props.dateRange),
                disabled: !matrixCode,
            }}
        >
            {
                !!matrices && matrices.length === 0 &&
                <EmptyCP description={'Nenhuma Matriz cadastrada no Desempenho de Coleta'}/>
            }
            {
                !!summary && !!monthRange &&
                <>
                    <SummaryHeaderSCP>
                        <div>
                            <BigNumberSCP>{`${importedPercent}%`}</BigNumberSCP>
                            <TextCP text={`dos pontos previstos com status "Importada" (${summary.totalImported} de ${summary.totalExpected})`} wrap={true}/>
                        </div>
                        <TagCP content={getPeriodLabel(monthRange)} color={'blue'}/>
                    </SummaryHeaderSCP>

                    <ProgressCP percent={importedPercent} showInfo={false} primaryColor={ThemeProject.primary}/>

                    <StatusLegendSCP>
                        {
                            Array.from(summary.totalsByStatus.entries()).map(([status, total]) => (
                                <TagCP
                                    key={status}
                                    small={true}
                                    color={CollectionStatusUtils.getColor(status)}
                                    content={`${CollectionStatusUtils.getLabel(status)}: ${total}`}
                                />
                            ))
                        }
                    </StatusLegendSCP>
                </>
            }
        </DashboardCardCP>
    )
}

function summarize(locationCodes: number[], grid?: CollectionGridResponseDTO): CollectionSummaryTP {

    const totalsByStatus = new Map<CollectionStatusEnum, number>()
    let totalExpected = 0
    for (const location of grid?.locations ?? []) {
        for (const cell of location.cells) {
            totalExpected++
            totalsByStatus.set(cell.status, (totalsByStatus.get(cell.status) ?? 0) + 1)
        }
    }

    return {
        locationCodes,
        totalExpected,
        totalImported: totalsByStatus.get(CollectionStatusEnum.IMPORTED) ?? 0,
        totalsByStatus,
    }
}

function getPeriodLabel(range: MonthRangeTP): string {
    const start = MonthRangeUtils.getFullMonthLabel(range.startYear, range.startMonth)
    if (range.startYear === range.endYear && range.startMonth === range.endMonth)
        return start

    return `${start} a ${MonthRangeUtils.getFullMonthLabel(range.endYear, range.endMonth)}`
}

const SummaryHeaderSCP = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
`

const BigNumberSCP = styled.div`
    font-size: 40px;
    font-weight: 600;
    line-height: 1.2;
    color: ${ThemeProject.primary};
`

const StatusLegendSCP = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 12px;
`
